/**
 * DashboardV2 — central dashboard (real data), redesigned to match the
 * approved command-center mockup exactly.
 *
 * Backed by a SINGLE aggregate call (GET /api/dashboard/overview) — no N+1.
 * Sections (top→bottom):
 *   Header (welcome + actions) · KPI strip · [onboarding checklist for new users]
 *   Row 1: Draw readiness at a glance + Next up
 *   Row 2: Active projects · Total budget · Tasks & phases
 *   Footer: Quick capture · Need help
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, ArrowRight, ArrowLeft, X, Building2, ChevronRight, Sparkles, Rocket,
  ListChecks, DollarSign, Wallet, Loader2, ArrowRightLeft, FileText, Users,
  Table2, ScanLine, CheckCircle2, Clock, Circle, Landmark, ClipboardList, Share2, Trash2, Loader2 as Spinner,
  Target, AlertTriangle, TrendingUp, TrendingDown, Zap, MessageCircle, FileClock,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { DemoRequestModal } from '@/components/DemoRequestModal';
import FixDrawItemsDrawer from '@/components/FixDrawItemsDrawer';

const NAVY = '#13294B';
const GREEN = '#5c9e33';

// Platform social-proof stats. Edit as the numbers grow.
const DR_TOTAL_FACILITATED = '$22.3M';
const DR_AVG_PER_PROJECT = '$425K';
const DR_AVG_TIME = '2.4 days';

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000) return `$${Math.round(v / 1000)}K`;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const fmtFull = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// ---- Readiness ring (dark hero) — large, tactile gradient dial ----
function ScoreRing({ score = 0, size = 188, stroke = 15, caption = 'GETTING CLOSER', onClick }) {
  const pct = Math.max(0, Math.min(100, score));
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const off = circ * (1 - pct / 100);
  const [, c2, capColor] = score >= 85
    ? ['#6ee7a8', '#16a34a', '#a8d977']
    : score >= 60
      ? ['#fbbf24', '#f97316', '#fdba74']
      : ['#fca5a5', '#ef4444', '#fca5a5'];
  const theta = (pct / 100) * 2 * Math.PI;
  const knobX = size / 2 + r * Math.cos(theta);
  const knobY = size / 2 + r * Math.sin(theta);
  return (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        className="relative flex-shrink-0 group focus:outline-none"
        style={{ width: size, height: size }}
        data-testid="v2-readiness-dial"
        aria-label={`Draw readiness ${Math.round(score)} of 100`}
      >
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none" stroke={c2} strokeWidth={stroke}
            strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.22,1,.36,1)' }}
          />
          {pct > 0 && pct < 100 && (
            <circle cx={knobX} cy={knobY} r={stroke / 2 - 2} fill="#fff" />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-5xl font-extrabold text-white tabular-nums leading-none">{Math.round(score)}</span>
          <span className="text-xs text-slate-400 tracking-[0.25em] mt-1.5">/100</span>
        </div>
      </button>
      <span className="text-[11px] font-bold tracking-[0.2em]" style={{ color: capColor }}>{caption}</span>
    </div>
  );
}

// ---- Small readiness ring (project rows) ----
function MiniRing({ score = 0, size = 40, stroke = 4 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const off = circ * (1 - Math.max(0, Math.min(100, score)) / 100);
  const color = score >= 85 ? '#059669' : score >= 60 ? '#d97706' : '#dc2626';
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold" style={{ color }}>{Math.round(score)}</span>
    </div>
  );
}

function StatusPill({ score }) {
  const [cls, label] = score >= 85
    ? ['bg-emerald-100 text-emerald-700', 'Bank-ready']
    : score >= 60
      ? ['bg-blue-100 text-blue-700', 'In progress']
      : ['bg-amber-100 text-amber-700', 'Needs work'];
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${cls}`}>{label}</span>;
}

function KpiTile({ icon: Icon, value, label, trend, tint, iconColor }) {
  return (
    <div className="flex items-start gap-4 px-6 py-5" data-testid={`v2-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
      <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: tint }}>
        <Icon className="w-5 h-5" style={{ color: iconColor }} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-extrabold text-slate-900 tabular-nums leading-tight">{value}</p>
        <p className="text-[13px] text-slate-500 leading-snug mt-0.5">{label}</p>
        {trend && <div className="text-[11px] mt-1.5 font-medium">{trend}</div>}
      </div>
    </div>
  );
}

const TrendUp = ({ children }) => (
  <span className="text-emerald-600 flex items-center gap-1"><TrendingUp className="w-3 h-3" />{children}</span>
);
const TrendDown = ({ children }) => (
  <span className="text-emerald-600 flex items-center gap-1"><TrendingDown className="w-3 h-3" />{children}</span>
);
const TrendFlat = ({ children }) => (
  <span className="text-slate-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" />{children}</span>
);

// ------------------------------------------------------ Start-project wizard
function StartProjectWizard({ onClose, onNotFunded, onFunded }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="v2-start-wizard-overlay"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="v2-start-wizard"
      >
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-900 flex-1">Start a project</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          <p className="text-sm text-slate-600 mb-4">Is this project <b>already funded</b>?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={onNotFunded}
              data-testid="v2-wiz-not-funded"
              className="text-left rounded-lg border-2 border-slate-200 p-5 hover:border-blue-400 hover:bg-blue-50 transition"
            >
              <ClipboardList className="w-6 h-6 text-blue-600" />
              <p className="font-bold text-slate-900 mt-3">No, not funded yet</p>
              <p className="text-xs text-slate-500 mt-1">We&apos;ll take you into the <b>Deal Pipeline</b> to collect bids and get bank-ready.</p>
            </button>
            <button
              onClick={onFunded}
              data-testid="v2-wiz-funded"
              className="text-left rounded-lg border-2 border-slate-200 p-5 hover:border-emerald-400 hover:bg-emerald-50 transition"
            >
              <ArrowRightLeft className="w-6 h-6" style={{ color: GREEN }} />
              <p className="font-bold text-slate-900 mt-3">Yes, it&apos;s funded</p>
              <p className="text-xs text-slate-500 mt-1">Already underway — <b>migrate it in</b> and catch up to today.</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- page
export default function DashboardV2({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewOb = searchParams.get('onboarding') === 'preview';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('active');
  const [wizard, setWizard] = useState(false);
  const [obDismissed, setObDismissed] = useState(() => localStorage.getItem('dr_ob_dismiss') === '1');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [lenderDemoOpen, setLenderDemoOpen] = useState(false);
  const [fixOpen, setFixOpen] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const openFix = () => { if (focus) setFixOpen(true); };

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await apiService.getDashboardOverview(user.id);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError('Unable to load your dashboard');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  // Mobile browsers (esp. iOS Safari) suspend and abort in-flight requests when
  // the tab is backgrounded. On return, silently re-fetch so the dashboard
  // recovers instead of sticking on "Unable to load your dashboard".
  useEffect(() => {
    const onResume = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('online', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('online', onResume);
    };
  }, [refresh]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiService.deleteProject(deleteTarget.id);
      toast.success(`"${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      await refresh();
    } catch (e) {
      toast.error('Could not delete project. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const goMigrate = () => { setWizard(false); navigate('/migrate'); };
  const goPipeline = () => { setWizard(false); navigate('/project-hub?tab=pipeline&intro=bidding'); };

  const isDemo = !!localStorage.getItem('demo_session') || (user?.id || '').startsWith('demo-');
  const isDemoOwner = !!user?.feature_flags?.demo_owner_tools;
  const copyDemoLink = async () => {
    const link = `${window.location.origin}/try-demo-invite.html`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Demo link copied — share it anywhere', { description: link });
    } catch {
      toast('Copy this demo link', { description: link });
    }
  };
  const dismissOnboarding = () => { localStorage.setItem('dr_ob_dismiss', '1'); setObDismissed(true); };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="v2-loading">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: NAVY }} />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-slate-600 mb-4">{error || 'No data'}</p>
          <button
            data-testid="v2-dashboard-retry"
            onClick={() => { setError(null); setLoading(true); refresh(); }}
            className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-white font-semibold text-sm"
            style={{ background: NAVY }}
          >
            <Loader2 className="w-4 h-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const tasks = data.tasks || {};
  const upcoming = data.upcoming_draws || [];
  const projects = (data.projects || []).filter((p) => (view === 'active' ? !p.is_complete : p.is_complete));
  const projMap = Object.fromEntries((data.projects || []).map((p) => [p.id, p]));

  // Readiness focus = the active project with started work that still needs docs
  // (lowest score first). Projects with no started phases aren't actionable yet.
  const activeRows = (data.projects || []).filter((p) => !p.is_complete);
  const startedRows = activeRows.filter((p) => (p.started_phase_count ?? 0) > 0);
  const scorable = startedRows.filter((p) => (p.readiness_score ?? 0) < 100);
  // Default spotlight = started project furthest from bank-ready (lowest score).
  const defaultFocus = [...scorable].sort((a, b) => (a.readiness_score ?? 0) - (b.readiness_score ?? 0))[0]
    || [...startedRows].sort((a, b) => (a.readiness_score ?? 0) - (b.readiness_score ?? 0))[0]
    || [...activeRows].sort((a, b) => (a.readiness_score ?? 0) - (b.readiness_score ?? 0))[0]
    || (data.projects || [])[0];
  // …but the user can override via the project switcher.
  const focus = (focusId && activeRows.find((p) => p.id === focusId)) || defaultFocus;
  const focusItems = focus?.draw_items || [];
  const focusMissing = focusItems.filter((i) => !i.ok);
  const focusStarted = (focus?.started_phase_count ?? 0) > 0;
  const firstName = ((user?.name || user?.full_name || (user?.email ? user.email.split('@')[0] : '') || '')).split(' ')[0];

  // Onboarding checklist — reflects real progress; shows until complete/dismissed.
  const ob = data.onboarding || {};
  const pid = ob.first_project_id;
  const obSteps = [
    { key: 'step_project', label: 'Create your first project', cta: 'Start', onClick: () => navigate('/new-project-vision') },
    { key: 'step_loan', label: 'Add loan details', cta: 'Add', onClick: () => navigate(pid ? `/project/${pid}?tab=loan` : '/dashboard') },
    { key: 'step_contractors', label: 'Invite contractors', cta: 'Invite', onClick: () => navigate('/contractors') },
    { key: 'step_schedule', label: 'Build your draw schedule', cta: 'Build', onClick: () => navigate(pid ? `/project/${pid}` : '/dashboard') },
    { key: 'step_draw', label: 'Request your first draw', cta: 'Request', onClick: () => navigate(pid ? `/project/${pid}?tab=loan` : '/dashboard') },
  ];
  const obDone = obSteps.filter((s) => (previewOb ? false : ob[s.key])).length;
  const obActiveKey = obSteps.find((s) => (previewOb ? true : !ob[s.key]))?.key;
  const showOnboarding = previewOb || (!ob.complete && !obDismissed);
  const heroIsWelcome = previewOb || !data.has_projects;

  const secondaryBtn = "inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-700 font-semibold text-sm px-4 py-2.5 rounded-lg hover:bg-slate-50";

  return (
    <div className="min-h-screen bg-slate-100" data-testid="dashboard-v2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Header — welcome + primary actions */}
        <div className="flex items-start justify-between flex-wrap gap-4" data-testid="v2-welcome-header">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Welcome back{firstName ? `, ${firstName}` : ''}</h1>
            <p className="text-sm text-slate-500 mt-1">Here's what's happening across your projects.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isDemoOwner && (
              <button onClick={copyDemoLink} className={secondaryBtn} data-testid="v2-copy-demo-link-btn" title="Copy your public demo link">
                <Share2 className="w-4 h-4" /> Copy demo link
              </button>
            )}
            {isDemoOwner && (
              <button onClick={() => setLenderDemoOpen(true)} className={secondaryBtn} data-testid="v2-send-lender-demo-btn" title="Email a lender the read-only demo">
                <Building2 className="w-4 h-4" /> Send lender demo
              </button>
            )}
            <button onClick={goMigrate} className={secondaryBtn} data-testid="v2-import-existing-btn">
              <ArrowRightLeft className="w-4 h-4" /> Migrate project
            </button>
            <button onClick={() => navigate('/new-project-vision')} className="inline-flex items-center gap-2 text-white font-bold text-sm px-5 py-2.5 rounded-xl transition" style={{ background: NAVY }} data-testid="v2-start-project-btn">
              <Plus className="w-4 h-4" /> Start a project
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="v2-kpi-strip">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            <KpiTile icon={Landmark} value={`${DR_TOTAL_FACILITATED}+`} label="In draws processed through DrawReady" trend={<TrendUp>18% this month</TrendUp>} tint="rgba(92,158,51,0.12)" iconColor={GREEN} />
            <KpiTile icon={DollarSign} value={DR_AVG_PER_PROJECT} label="Average draw requested per project" trend={<TrendUp>12% this month</TrendUp>} tint="rgba(11,59,111,0.10)" iconColor={NAVY} />
            <KpiTile icon={FileText} value={`${data.active_projects || 0}`} label="Active projects" trend={<TrendFlat>{data.new_this_month || 0} new this month</TrendFlat>} tint="rgba(79,70,229,0.10)" iconColor="#4f46e5" />
            <KpiTile icon={Clock} value={DR_AVG_TIME} label="Average time to bank-ready" trend={<TrendDown>1.1 days vs last month</TrendDown>} tint="rgba(217,119,6,0.12)" iconColor="#d97706" />
          </div>
        </div>

        {/* Onboarding checklist (new users only) */}
        {showOnboarding && (
          <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="v2-getting-started">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-bold text-slate-800 flex items-center gap-2"><ListChecks className="w-4 h-4" style={{ color: GREEN }} /> Getting started</p>
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-slate-400" data-testid="v2-getting-started-progress">{obDone} of 5</span>
                <button onClick={dismissOnboarding} className="text-xs text-slate-400 hover:text-slate-600" data-testid="v2-getting-started-dismiss">Hide</button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-3">Five steps to your first bank-ready draw.</p>
            <div className="space-y-1.5">
              {obSteps.map((s) => {
                const done = previewOb ? false : !!ob[s.key];
                const active = s.key === obActiveKey;
                return (
                  <div key={s.key} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${active ? 'bg-blue-50 border border-blue-200' : ''}`} data-testid={`v2-ob-step-${s.key}`}>
                    {done ? <CheckCircle2 className="w-4 h-4" style={{ color: GREEN }} /> : active ? <Clock className="w-4 h-4 text-blue-600" /> : <Circle className="w-4 h-4 text-slate-300" />}
                    <span className={`text-sm flex-1 ${done ? 'text-slate-400 line-through' : active ? 'font-bold text-slate-900' : 'text-slate-500'}`}>{s.label}</span>
                    {active && (
                      <button onClick={s.onClick} className="text-xs font-bold text-white px-3 py-1 rounded-lg" style={{ background: NAVY }} data-testid="v2-getting-started-cta">{s.cta}</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ROW 1 — Draw readiness at a glance + Next up */}
        {heroIsWelcome ? (
          <div className="rounded-lg p-7 sm:p-9 text-white relative overflow-hidden" style={{ background: NAVY }} data-testid="v2-onboarding-hero">
            <div className="absolute -right-8 -top-10 w-44 h-44 rounded-full opacity-20" style={{ background: GREEN }} />
            <div className="relative max-w-2xl">
              <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#a8d977' }}>Welcome to DrawReady</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold mt-1">Set up your first project</h2>
              <p className="text-blue-100 mt-2 text-sm">Just starting out, or already mid-build? Tell us where you are and we&apos;ll take it from there — then track budgets, phases and bank draws in one place.</p>
              <div className="flex flex-wrap gap-3 mt-5">
                <button onClick={() => navigate('/new-project-vision')} className="inline-flex items-center gap-2 bg-white text-sm font-bold px-5 py-3 rounded-lg hover:bg-slate-100 transition" style={{ color: NAVY }} data-testid="v2-hero-start">
                  <Plus className="w-4 h-4" /> Start a new project
                </button>
                <button onClick={goMigrate} className="inline-flex items-center gap-2 bg-white/15 border border-white/30 text-white text-sm font-bold px-5 py-3 rounded-xl hover:bg-white/25 transition" data-testid="v2-hero-migrate">
                  <ArrowRightLeft className="w-4 h-4" /> I already have one underway
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Draw readiness at a glance */}
            <div className="lg:col-span-2 rounded-lg p-6 text-white relative overflow-hidden" style={{ background: NAVY }} data-testid="v2-readiness-hero">
              <div className="flex items-center justify-between gap-3 mb-5">
                <h3 className="text-lg font-bold">Draw readiness at a glance</h3>
                {startedRows.length > 1 && (
                  <select
                    data-testid="v2-readiness-project-switcher"
                    value={focus?.id || ''}
                    onChange={(e) => setFocusId(e.target.value)}
                    className="bg-white/10 text-white text-sm rounded-lg pl-3 pr-8 py-1.5 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/40 max-w-[55%] truncate cursor-pointer"
                    aria-label="Switch project"
                  >
                    {startedRows.map((p) => (
                      <option key={p.id} value={p.id} className="text-slate-900">
                        {p.name} · {Math.round(p.readiness_score ?? 0)}/100
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-7">
                <ScoreRing
                  score={focus?.readiness_score ?? 0}
                  caption={focus && (focus.readiness_score ?? 0) >= 85 ? 'BANK-READY' : (focus?.readiness_score ?? 0) >= 60 ? 'GETTING CLOSER' : 'NEEDS WORK'}
                  onClick={() => { if (focusStarted && focusMissing.length > 0) openFix(); else if (focus) navigate(`/project/${focus.id}?tab=loan`); }}
                />
                <div className="flex-1 w-full">
                  {focus ? (
                    <>
                      <h4 className="text-lg font-bold">
                        {!focusStarted
                          ? 'Start work to begin your draw'
                          : focusMissing.length === 0
                            ? "You're bank-ready 🎉"
                            : `You're close! ${focusMissing.length} item${focusMissing.length === 1 ? '' : 's'} to fix.`}
                      </h4>
                      <p className="text-sm text-blue-100 mb-4">
                        {!focusStarted
                          ? `No phases have started on ${focus.name} yet. Once work begins, we'll track the docs each line item needs.`
                          : focusMissing.length === 0
                            ? `${focus.name} has every required document for its started work.`
                            : 'Collect the missing documents below to reach 100 and submit a bank-ready draw.'}
                      </p>
                      {focusItems.length > 0 && (
                        <div className="space-y-1.5 mb-4">
                          {focusItems.map((it) => (
                            <button
                              key={it.label}
                              onClick={() => { if (focusStarted && focusMissing.length > 0) openFix(); else if (focus) navigate(`/project/${focus.id}?tab=loan`); }}
                              className="w-full flex items-center gap-3 bg-white/[0.06] hover:bg-white/[0.12] rounded-lg px-3 py-2.5 transition text-left"
                              data-testid={`v2-readiness-item-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {it.ok
                                ? <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: '#a8d977' }} />
                                : <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />}
                              <span className="text-sm text-white flex-1">{it.label}</span>
                              <span className={`text-xs font-semibold ${it.ok ? 'text-[#a8d977]' : 'text-amber-300'}`}>
                                {it.ok ? 'Collected' : (it.count > 1 ? `${it.count} needed` : 'Missing')}
                              </span>
                              <ChevronRight className="w-4 h-4 text-white/40" />
                            </button>
                          ))}
                        </div>
                      )}
                      <button
                        onClick={() => { if (!focusStarted) navigate(`/project/${focus.id}?tab=loan`); else if (focusMissing.length === 0) navigate(`/project/${focus.id}?tab=loan`); else openFix(); }}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#1E4ED8] hover:bg-[#1740AE] text-white text-sm font-bold px-5 py-3 rounded-xl transition"
                        data-testid="v2-fix-draw-items-btn"
                      >
                        {!focusStarted ? 'Open project' : focusMissing.length === 0 ? 'Request a draw' : 'Fix my draw items'} <ArrowRight className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <h4 className="text-lg font-bold">Add a project to see your draw readiness.</h4>
                  )}
                </div>
              </div>
            </div>

            {/* Next up */}
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden flex flex-col" data-testid="v2-next-up">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-bold text-slate-900">Next up</h3>
                <span className="text-xs text-slate-400">{upcoming.length} item{upcoming.length === 1 ? '' : 's'}</span>
              </div>
              <div className="flex-1 overflow-y-auto max-h-[228px]">
                {upcoming.length === 0 ? (
                  <div className="px-5 py-10 text-center text-sm text-slate-400">
                    <Clock className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                    No draws coming up. Start work on a line item and it'll show here.
                  </div>
                ) : (
                  upcoming.map((d) => {
                    const rp = projMap[d.project_id];
                    const ready = (rp?.readiness_score ?? 0) >= 85;
                    return (
                      <button
                        key={d.project_id}
                        onClick={() => navigate(`/project/${d.project_id}?tab=budget${d.phase_ids?.length ? `#phase-${d.phase_ids[0]}` : ''}`)}
                        className="w-full text-left px-5 py-3.5 flex items-center gap-3 border-b border-slate-50 last:border-0 hover:bg-slate-50"
                        data-testid={`v2-draw-${d.project_id}`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                          <DollarSign className="w-4 h-4" style={{ color: GREEN }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{d.project_name}</p>
                          <p className="text-[11px] text-slate-400">{d.drawn_pct}% drawn · Est. {fmtFull(d.available)}</p>
                        </div>
                        <span className={`text-[11px] font-semibold shrink-0 ${ready ? 'text-emerald-600' : 'text-blue-600'}`}>{ready ? 'Ready soon' : 'In progress'}</span>
                      </button>
                    );
                  })
                )}
              </div>
              <div className="px-5 py-3 border-t border-slate-100">
                <button onClick={() => navigate('/project-hub')} className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid="v2-view-all-draws">
                  View all upcoming draws <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ROW 2 — Active projects · Total budget · Tasks & phases */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Active projects */}
          <div className="lg:col-span-5 rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="v2-projects-list">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-900 capitalize">{view === 'active' ? 'Active' : 'Completed'} projects</h3>
              <button onClick={() => navigate('/project-hub')} className="text-xs font-semibold text-slate-500 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50" data-testid="v2-view-all-projects">View all</button>
            </div>
            {projects.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-slate-400">No {view} projects yet.</div>
            ) : (
              <div>
                <div className="grid grid-cols-[1fr_64px_64px_54px] gap-2 px-5 py-2 text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <span>Project</span><span className="text-right">Drawn</span><span className="text-right">Budget</span><span className="text-center">Ready</span>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                {projects.map((p) => (
                  <div
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/project/${p.id}`)}
                    className="grid grid-cols-[1fr_64px_64px_54px] gap-2 items-center px-5 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer group"
                    data-testid={`v2-project-${p.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <StatusPill score={p.readiness_score ?? 0} />
                        {!isDemo && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
                            className="opacity-0 group-hover:opacity-100 transition text-slate-300 hover:text-red-600"
                            title="Delete project"
                            data-testid={`v2-delete-project-${p.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono text-slate-700">{p.drawn_pct}%</p>
                      <p className="text-[10px] text-slate-400">{fmtMoney(p.amount_drawn)}</p>
                    </div>
                    <p className="text-right text-sm font-mono text-slate-700">{fmtMoney(p.total_budget || p.loan_principal_amount)}</p>
                    <div className="flex justify-center"><MiniRing score={p.readiness_score ?? 0} /></div>
                  </div>
                ))}
                </div>
              </div>
            )}
            {data.total_projects > 0 && (
              <div className="px-5 py-3 border-t border-slate-100">
                <button
                  onClick={() => setView(view === 'active' ? 'completed' : 'active')}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                  data-testid="v2-toggle-archived"
                >
                  {view === 'active' ? `Show completed projects (${data.completed_projects || 0})` : 'Show active projects'}
                </button>
              </div>
            )}
          </div>

          {/* Total budget */}
          <div className="lg:col-span-3 rounded-lg p-5 text-white" style={{ background: NAVY }} data-testid="v2-portfolio">
            <p className="text-sm font-semibold text-blue-100">Total budget across all projects</p>
            <p className="text-3xl font-extrabold mt-1 tabular-nums" data-testid="v2-total-budget">{fmtFull(data.total_budget)}</p>
            <div className="mt-4 space-y-2.5 text-sm border-t border-white/15 pt-3">
              <div className="flex justify-between"><span className="text-blue-200 flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> Total loans</span><span className="font-semibold">{fmtMoney(data.total_loan_principal)}</span></div>
              <div className="flex justify-between"><span className="text-blue-200 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Drawn to date</span><span className="font-semibold">{fmtMoney(data.total_drawn)}</span></div>
              <div className="flex justify-between"><span className="text-blue-200 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Available to draw</span><span className="font-semibold" style={{ color: '#a8d977' }}>{fmtMoney(data.total_available_for_draw)}</span></div>
              <div className="flex justify-between"><span className="text-blue-200 flex items-center gap-1.5"><FileClock className="w-3.5 h-3.5" /> Projected overage</span><span className="font-semibold">{fmtFull(data.projected_overage || 0)}</span></div>
            </div>
            <button onClick={() => navigate('/reports')} className="mt-4 text-xs font-semibold text-blue-300 hover:text-blue-200 flex items-center gap-1" data-testid="v2-budget-view">
              View budget vs. actual <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          {/* Tasks & phases */}
          <div className="lg:col-span-4 rounded-lg border border-slate-200 bg-white p-5" data-testid="v2-tasks">
            <p className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><ListChecks className="w-4 h-4" style={{ color: GREEN }} /> Tasks &amp; phases</p>
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              {[['In progress', tasks.in_progress || 0, 'text-blue-600', true], ['Scheduled', tasks.scheduled || 0, 'text-slate-700', false], ['Completed', tasks.completed || 0, 'text-emerald-600', false]].map(([l, v, c, hl]) => (
                <div key={l} className={`rounded-xl py-2.5 border ${hl ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                  <p className={`text-xl font-extrabold ${c}`}>{v}</p>
                  <p className="text-[10px] text-slate-500">{l}</p>
                </div>
              ))}
            </div>
            {(tasks.recent_tasks || []).length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-2">No tasks yet.</p>
            ) : (
              <div className="divide-y divide-slate-50">
                {(tasks.recent_tasks || []).slice(0, 5).map((t) => {
                  const done = t.status === 'completed';
                  const active = t.status === 'in_progress';
                  const label = done ? 'Completed' : active ? 'In progress' : 'Scheduled';
                  const cls = done ? 'text-emerald-600' : active ? 'text-blue-600' : 'text-slate-400';
                  return (
                    <button
                      key={t.id}
                      onClick={() => t.project_id && navigate(`/project/${t.project_id}?tab=budget`)}
                      className="w-full text-left flex items-center gap-2 py-2 hover:bg-slate-50 rounded-lg px-1"
                      data-testid={`v2-task-${t.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-medium truncate ${done ? 'text-slate-500' : 'text-slate-800'}`}>{t.name}</p>
                        <p className="text-[10px] text-slate-400 truncate">{t.project_name}</p>
                      </div>
                      <span className={`text-[11px] font-semibold shrink-0 ${cls}`}>{label}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
            <button onClick={() => navigate('/schedule')} className="mt-3 text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid="v2-view-schedule">
              View all tasks &amp; phases <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Footer — help */}
        <div className="grid grid-cols-1 gap-4" data-testid="v2-quick-actions">
          <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-5 py-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(92,158,51,0.12)' }}><MessageCircle className="w-5 h-5" style={{ color: GREEN }} /></div>
            <div className="flex-1"><p className="font-semibold text-slate-900 text-sm">Need help?</p><p className="text-xs text-slate-500">Chat with support.</p></div>
            <button onClick={() => { window.location.href = 'mailto:support@drawready.com?subject=DrawReady%20support'; }} className="text-sm font-semibold text-slate-700 border border-slate-200 rounded-xl px-4 py-2 hover:bg-slate-50" data-testid="v2-open-chat">Open chat</button>
          </div>
        </div>
      </div>

      {wizard && <StartProjectWizard onClose={() => setWizard(false)} onNotFunded={goPipeline} onFunded={goMigrate} />}

      <FixDrawItemsDrawer
        open={fixOpen}
        onClose={() => setFixOpen(false)}
        projectId={focus?.id}
        onChanged={refresh}
      />

      <DemoRequestModal
        open={lenderDemoOpen}
        onClose={() => setLenderDemoOpen(false)}
        endpoint="/lender-demo/invite"
        title="Send lender demo"
        subtitle="Email a lender the read-only portfolio demo + a borrower demo they can forward to clients."
        submitLabel="Send lender demo"
        successTitle="Lender demo sent"
        companyLabel="Institution"
        footnote="We'll email them the lender portfolio demo, plus a borrower demo link they can share with their clients — from noreply@drawready.com."
      />

      {/* Delete project confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4" onClick={() => !deleting && setDeleteTarget(null)} data-testid="v2-delete-overlay">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="v2-delete-modal">
            <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center mb-3">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Delete this project?</h3>
            <p className="text-sm text-slate-500 mt-1">
              <b className="text-slate-700">{deleteTarget.name}</b> and all of its budget, phases, draws and bid data will be permanently removed. This can&apos;t be undone.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="text-sm font-semibold text-slate-600 px-4 py-2.5 rounded-xl hover:bg-slate-100 disabled:opacity-60" data-testid="v2-delete-cancel">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} className="inline-flex items-center gap-2 text-sm font-bold text-white px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-60" data-testid="v2-delete-confirm">
                {deleting ? <Spinner className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Deleting…' : 'Delete project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
